import React, { useState, useEffect } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ScatterChart, Scatter, ComposedChart, Area } from 'recharts';
import { TrendingUp, TrendingDown, Trophy, Calendar, Filter, Target, Award, Zap, BarChart3, History, Clock, X, Eye } from 'lucide-react';
import PowerRankingsBanner from './PowerRankingsBanner';

const PowerRankings = ({ h2hData, finalData, currentYearPR, allTimeRank, loading: propLoading, error: propError }) => {
  const [rankingsType, setRankingsType] = useState('Current Season');
  const [includePlayoffs, setIncludePlayoffs] = useState('No');
  const [rankingsData, setRankingsData] = useState([]);
  const [progressionData, setProgressionData] = useState({});
  const [allTimeProgression, setAllTimeProgression] = useState({});
  const [currentSeasonYear, setCurrentSeasonYear] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [modalImage, setModalImage] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  useEffect(() => {
    if (propError) {
      setError(propError);
      setLoading(false);
      return;
    }

    if (propLoading) {
      setLoading(true);
      return;
    }

    if (currentYearPR && allTimeRank && currentYearPR.length > 0 && allTimeRank.length > 0) {
      loadPowerRankingsData();
    }
  }, [currentYearPR, allTimeRank, propLoading, propError, rankingsType, includePlayoffs]);

  const loadPowerRankingsData = () => {
    setLoading(true);
    setError(null);
    
    try {
      console.log('Loading power rankings data from sheets...');
      console.log('CurrentYearPR records:', currentYearPR?.length || 0);
      console.log('AllTimeRank records:', allTimeRank?.length || 0);

      if (!currentYearPR || currentYearPR.length === 0) {
        throw new Error('No CurrentYearPR data available');
      }

      if (!allTimeRank || allTimeRank.length === 0) {
        throw new Error('No AllTimeRank data available');
      }

      // Find the most recent season
      const allYears = [...new Set([
        ...allTimeRank.map(r => r.Year),
        ...currentYearPR.map(r => r.Year)
      ])].filter(y => y && !isNaN(y));
      
      const currentSeasonYear = Math.max(...allYears);
      setCurrentSeasonYear(currentSeasonYear);
      console.log('Current season year:', currentSeasonYear);

      if (rankingsType === 'Current Season') {
        loadCurrentSeasonRankings(currentSeasonYear);
      } else {
        loadAllTimeRankings();
      }

    } catch (error) {
      console.error('Error loading power rankings data:', error);
      setError('Failed to load power rankings data: ' + error.message);
    }
    setLoading(false);
  };

  const loadCurrentSeasonRankings = (currentSeasonYear) => {
    console.log('Loading current season rankings...');
    
    const playoffFilter = includePlayoffs === 'No';
    
    // Filter for current season
    const currentSeasonData = currentYearPR.filter(r => {
      const matchesYear = r.Year === currentSeasonYear;
      const matchesPlayoffs = !playoffFilter || r.Playoffs === 'No';
      return matchesYear && matchesPlayoffs;
    });

    if (currentSeasonData.length === 0) {
      throw new Error(`No current season power rankings data found for ${currentSeasonYear}`);
    }

    // Calculate actual H2H records from h2hData
    const actualRecords = {};
    if (h2hData && h2hData.length > 0) {
      const currentSeasonH2H = h2hData.filter(game => {
        const matchesYear = game.Year === currentSeasonYear;
        let matchesGameType = true;
        
        if (playoffFilter) {
          // Only include Regular Season games when playoffs are excluded
          matchesGameType = game.Type === 'Regular Season';
        }
        // When playoffs are included, include all game types
        
        return matchesYear && matchesGameType;
      });

      console.log('H2H games for traditional records:', currentSeasonH2H.length);
      console.log('Sample game types:', [...new Set(currentSeasonH2H.map(g => g.Type))]);

      currentSeasonH2H.forEach(game => {
        if (!actualRecords[game.Manager1]) {
          actualRecords[game.Manager1] = { wins: 0, losses: 0 };
        }
        actualRecords[game.Manager1].wins += (game.IsWin || 0);
        actualRecords[game.Manager1].losses += (game.IsLoss || 0);
      });

      console.log('Actual records calculated:', actualRecords);
    }

    // Find the latest week
    const latestWeek = Math.max(...currentSeasonData.map(r => r.Week));
    console.log('Latest week in current season:', latestWeek);

    // Get current rankings for the latest week
    const currentRankings = currentSeasonData
      .filter(r => r.Week === latestWeek)
      .sort((a, b) => a.Rank - b.Rank)
      .map(manager => ({
        ...manager,
        actualWins: actualRecords[manager.Manager1]?.wins || 0,
        actualLosses: actualRecords[manager.Manager1]?.losses || 0
      }));

    console.log('Current season rankings:', currentRankings.length, 'managers');

    // Build progression data
    const progression = {};
    const allWeeks = [...new Set(currentSeasonData.map(r => r.Week))].sort((a, b) => a - b);
    
    currentSeasonData.forEach(record => {
      if (!progression[record.Manager1]) {
        progression[record.Manager1] = [];
      }
      
      progression[record.Manager1].push({
        week: record.Week,
        rank: record.Rank,
        winPct: parseFloat((record.cumWinPct * 100).toFixed(1)),
        wins: record.cumWins,
        losses: record.cumLosses,
        pf: record.PF ? parseFloat(record.PF.toFixed(2)) : 0
      });
    });

    // Sort progression data by week
    Object.keys(progression).forEach(manager => {
      progression[manager].sort((a, b) => a.week - b.week);
    });

    console.log('Current season rankings loaded successfully');
    setRankingsData(currentRankings);
    setProgressionData(progression);
  };

  const loadAllTimeRankings = () => {
    console.log('Loading all-time rankings...');
    
    const playoffFilter = includePlayoffs === 'No';
    
    // Filter all-time data  
    const filteredAllTimeData = allTimeRank.filter(r => {
      const matchesPlayoffs = !playoffFilter || r.Playoffs === 'No';
      return matchesPlayoffs;
    });

    if (filteredAllTimeData.length === 0) {
      throw new Error('No all-time power rankings data found');
    }

    // Calculate traditional H2H records for all-time using the same logic as AllTimeStandings
    const traditionalRecords = {};
    if (h2hData && h2hData.length > 0) {
      console.log('Processing H2H data for traditional records...');
      console.log('Sample H2H record:', h2hData[0]);
      console.log('Total H2H records:', h2hData.length);
      
      // Filter H2H data based on the same criteria as power rankings
      const filteredH2H = h2hData.filter(game => {
        const matchesPlayoffs = !playoffFilter || game.Type === 'Regular Season';
        const hasValidData = game.Manager1 && 
          (game.PF !== undefined && game.PF !== null) &&
          (game.PA !== undefined && game.PA !== null) &&
          (game.IsWin !== undefined && game.IsWin !== null) &&
          (game.IsLoss !== undefined && game.IsLoss !== null);
        
        return matchesPlayoffs && hasValidData;
      });

      console.log('Filtered H2H records for traditional calculation:', filteredH2H.length);
      if (filteredH2H.length > 0) {
        console.log('Sample filtered record:', filteredH2H[0]);
        console.log('IsWin values sample:', filteredH2H.slice(0, 5).map(g => g.IsWin));
        console.log('IsLoss values sample:', filteredH2H.slice(0, 5).map(g => g.IsLoss));
      }

      // Calculate traditional H2H wins/losses (same as AllTimeStandings logic)
      filteredH2H.forEach(game => {
        const manager = game.Manager1;
        
        if (!traditionalRecords[manager]) {
          traditionalRecords[manager] = { wins: 0, losses: 0 };
        }
        
        const isWin = parseInt(game.IsWin) || 0;
        const isLoss = parseInt(game.IsLoss) || 0;
        
        traditionalRecords[manager].wins += isWin;
        traditionalRecords[manager].losses += isLoss;
      });

      console.log('Traditional all-time records calculated:', traditionalRecords);
      
      // Log comparison with all-play records
      const sampleManager = Object.keys(traditionalRecords)[0];
      if (sampleManager) {
        console.log(`Sample comparison for ${sampleManager}:`);
        console.log('Traditional H2H:', traditionalRecords[sampleManager]);
        
        const allPlayRecord = filteredAllTimeData.find(r => r.WeekID === Math.max(...filteredAllTimeData.map(r => r.WeekID)) && r.Manager1 === sampleManager);
        if (allPlayRecord) {
          console.log('All-Play:', { wins: allPlayRecord.cumWins, losses: allPlayRecord.cumLosses });
        }
      }
    }

    // Find the latest week ID
    const latestWeekID = Math.max(...filteredAllTimeData.map(r => r.WeekID));
    console.log('Latest WeekID in all-time data:', latestWeekID);

    // Get all-time rankings for the latest week and add traditional records
    const allTimeRankings = filteredAllTimeData
      .filter(r => r.WeekID === latestWeekID)
      .sort((a, b) => a.Rank - b.Rank)
      .map(manager => ({
        ...manager,
        traditionalWins: traditionalRecords[manager.Manager1]?.wins || 0,
        traditionalLosses: traditionalRecords[manager.Manager1]?.losses || 0
      }));

    console.log('All-time rankings:', allTimeRankings.length, 'managers');

    // Build comprehensive all-time progression data - USE ALL DATA POINTS
    const allTimeProgression = {};
    
    // Process ALL filtered data points for rich visualization
    filteredAllTimeData.forEach(record => {
      const manager = record.Manager1;
      if (!allTimeProgression[manager]) {
        allTimeProgression[manager] = [];
      }
      
      allTimeProgression[manager].push({
        weekID: record.WeekID,
        year: record.Year,
        week: record.Week,
        rank: record.Rank,
        winPct: parseFloat((record.cumWinPct * 100).toFixed(1)),
        wins: record.cumWins,
        losses: record.cumLosses,
        games: record.cumGP || (record.cumWins + record.cumLosses),
        // Create a continuous timeline point for visualization
        timePoint: record.WeekID
      });
    });

    // Sort all data by WeekID for proper timeline visualization
    Object.keys(allTimeProgression).forEach(manager => {
      allTimeProgression[manager].sort((a, b) => a.weekID - b.weekID);
    });

    console.log('All-time progression data prepared with all data points');
    setRankingsData(allTimeRankings);
    setAllTimeProgression(allTimeProgression);
    setProgressionData({}); // Clear current season data
  };

  const getRankBadgeColor = (rank) => {
    if (rank === 1) return 'bg-gradient-to-br from-yellow-400 via-yellow-500 to-amber-600 text-white shadow-xl border border-yellow-300';
    if (rank === 2) return 'bg-gradient-to-br from-gray-300 via-gray-400 to-gray-500 text-white shadow-xl border border-gray-200';
    if (rank === 3) return 'bg-gradient-to-br from-amber-500 via-orange-500 to-amber-600 text-white shadow-xl border border-amber-400';
    if (rank <= 6) return 'bg-gradient-to-br from-emerald-500 via-green-500 to-emerald-600 text-white shadow-lg';
    if (rank <= 9) return 'bg-gradient-to-br from-blue-500 via-indigo-500 to-blue-600 text-white shadow-lg';
    return 'bg-gradient-to-br from-red-500 via-rose-500 to-red-600 text-white shadow-lg';
  };

  const getRankIcon = (rank) => {
    if (rank === 1) return <Trophy className="w-6 h-6" />;
    if (rank === 2) return <Award className="w-5 h-5" />;
    if (rank === 3) return <Target className="w-5 h-5" />;
    if (rank <= 6) return <TrendingUp className="w-4 h-4" />;
    if (rank > 9) return <TrendingDown className="w-4 h-4" />;
    return <Zap className="w-4 h-4" />;
  };

  // Modal functions
  const openModal = (manager, rank) => {
    setModalImage({ manager, rank, src: getManagerHeadshot(manager, rank) });
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setModalImage(null);
  };

  // Close modal on escape key
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        closeModal();
      }
    };

    if (isModalOpen) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden'; // Prevent background scrolling
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [isModalOpen]);

  // Helper function to get manager headshot based on rank
  const getManagerHeadshot = (manager, rank, isBackground = false) => {
    let emotion = 'main';
    if (rank >= 1 && rank <= 5) emotion = 'happy';
    else if (rank >= 6 && rank <= 7) emotion = 'main';
    else if (rank >= 8 && rank <= 12) emotion = 'angry';
    
    const filename = `${manager.toLowerCase()}${emotion}.png`;
    
    if (isBackground) {
      return {
        backgroundImage: `url("/${filename}")`,
        backgroundSize: 'contain',
        backgroundRepeat: 'no-repeat',
        backgroundPosition: 'center',
        opacity: '0.15'
      };
    }
    
    return `/${filename}`;
  };

  const getManagerProgression = (manager) => {
    if (rankingsType === 'All-Time') {
      // Return all data points for rich all-time visualization
      return allTimeProgression[manager] || [];
    } else {
      // Current season weekly progression - fix the rank calculation
      const data = progressionData[manager] || [];
      return data.map(item => ({
        week: item.week,
        rank: item.rank,
        displayRank: 13 - item.rank // Invert for chart display (13 - rank for 12 managers)
      }));
    }
  };

  const getLastWeekRank = (manager) => {
    if (rankingsType === 'All-Time') {
      const data = allTimeProgression[manager] || [];
      if (data.length < 2) return null;
      return data[data.length - 2].rank;
    } else {
      const data = progressionData[manager] || [];
      if (data.length < 2) return null;
      return data[data.length - 2].rank;
    }
  };

  const getRankChange = (currentRank, lastRank) => {
    if (!lastRank) return null;
    const change = lastRank - currentRank;
    if (change > 0) return { direction: 'up', amount: change };
    if (change < 0) return { direction: 'down', amount: Math.abs(change) };
    return { direction: 'same', amount: 0 };
  };

  const formatRankChange = (change) => {
    if (!change) return null;
    
    if (change.direction === 'same') {
      return 'Even';
    } else if (change.direction === 'up') {
      return `+${change.amount}`;
    } else {
      return `-${change.amount}`;
    }
  };

  const getPerformanceColor = (winPct) => {
    if (winPct >= 70) return 'text-emerald-600';
    if (winPct >= 60) return 'text-green-600';
    if (winPct >= 50) return 'text-blue-600';
    if (winPct >= 40) return 'text-orange-600';
    return 'text-red-600';
  };

  const getRankColor = (rank) => {
    // Enhanced color mapping for better visual distinction
    if (rank <= 1) return "#059669"; // Emerald-600
    if (rank <= 3) return "#10b981"; // Emerald-500  
    if (rank <= 5) return "#34d399"; // Emerald-400
    if (rank <= 7) return "#84cc16"; // Lime-500
    if (rank <= 9) return "#eab308"; // Yellow-500
    if (rank <= 11) return "#f97316"; // Orange-500
    return "#ef4444"; // Red-500
  };

  const formatTotalGames = (games) => {
    if (games >= 1000) {
      return `${(games / 1000).toFixed(1)}k`;
    }
    return games.toString();
  };

  const getAllTimeStats = (manager) => {
    if (rankingsType !== 'All-Time' || !allTimeProgression[manager]) return null;
    
    const data = allTimeProgression[manager];
    const totalYears = [...new Set(data.map(d => d.year))].length;
    const finalRecord = data[data.length - 1];
    const bestRank = Math.min(...data.map(d => d.rank));
    const worstRank = Math.max(...data.map(d => d.rank));
    
    // Find the most recent time the peak rank was achieved
    const peakAchievements = data.filter(d => d.rank === bestRank);
    const mostRecentPeak = peakAchievements[peakAchievements.length - 1];
    
    // Find the most recent time the trough (worst) rank was achieved
    const troughAchievements = data.filter(d => d.rank === worstRank);
    const mostRecentTrough = troughAchievements[troughAchievements.length - 1];
    
    return {
      totalYears,
      totalGames: finalRecord.games,
      bestRank,
      worstRank,
      currentStreak: calculateCurrentStreak(data),
      peakDate: mostRecentPeak ? `${mostRecentPeak.year} Week ${mostRecentPeak.week}` : null,
      troughDate: mostRecentTrough ? `${mostRecentTrough.year} Week ${mostRecentTrough.week}` : null
    };
  };

  const calculateCurrentStreak = (data) => {
    if (data.length < 2) return null;
    
    const recent = data.slice(-10); // Last 10 data points
    let streak = 0;
    let direction = null;
    
    for (let i = recent.length - 1; i > 0; i--) {
      const current = recent[i].rank;
      const previous = recent[i - 1].rank;
      const change = previous - current; // Positive = rank improved (moved up)
      
      if (direction === null) {
        direction = change > 0 ? 'up' : change < 0 ? 'down' : 'same';
        streak = Math.abs(change);
      } else if ((direction === 'up' && change > 0) || (direction === 'down' && change < 0)) {
        streak += Math.abs(change);
      } else {
        break;
      }
    }
    
    return { direction, amount: streak };
  };

  const createDynamicGradient = (chartData, managerId) => {
    if (!chartData || chartData.length < 2) return null;
    
    const stops = chartData.map((point, index) => {
      const percentage = (index / (chartData.length - 1)) * 100;
      const color = getRankColor(point.rank);
      return { offset: `${percentage}%`, color: color };
    });
    
    return stops;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 p-6">
        <div className="max-w-6xl mx-auto">
          <div className="animate-pulse space-y-6">
            <div className="h-20 bg-slate-200 rounded-2xl"></div>
            <div className="h-12 bg-slate-200 rounded-xl"></div>
            {[...Array(12)].map((_, i) => (
              <div key={i} className="bg-white rounded-2xl p-6 shadow-lg">
                <div className="flex items-center space-x-4">
                  <div className="w-16 h-16 bg-slate-200 rounded-2xl"></div>
                  <div className="flex-1 space-y-2">
                    <div className="h-6 bg-slate-200 rounded w-1/3"></div>
                    <div className="h-4 bg-slate-200 rounded w-1/2"></div>
                    <div className="h-3 bg-slate-200 rounded w-1/4"></div>
                  </div>
                  <div className="w-80 h-20 bg-slate-200 rounded-xl"></div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 p-6">
        <div className="max-w-6xl mx-auto">
          <div className="bg-red-50 border border-red-200 rounded-2xl p-8 shadow-lg">
            <h2 className="text-2xl font-bold text-red-800 mb-4">Error Loading Power Rankings</h2>
            <p className="text-red-700 mb-6">{error}</p>
            <button 
              onClick={() => {
                setError(null);
                loadPowerRankingsData();
              }}
              className="px-6 py-3 bg-red-600 text-white rounded-xl hover:bg-red-700 transition-colors font-medium"
            >
              Retry
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (rankingsData.length === 0) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 p-6">
        <div className="max-w-6xl mx-auto">
          <div className="bg-yellow-50 border border-yellow-200 rounded-2xl p-8 shadow-lg">
            <h2 className="text-2xl font-bold text-yellow-800 mb-4">No Rankings Data</h2>
            <p className="text-yellow-700 mb-4">No power rankings data found for the selected criteria.</p>
            <div className="bg-yellow-100 rounded-xl p-4 mb-6 text-sm text-yellow-700">
              <p><strong>Current season:</strong> {currentSeasonYear || 'Unknown'}</p>
              <p><strong>Rankings type:</strong> {rankingsType}</p>
              <p><strong>Include playoffs:</strong> {includePlayoffs}</p>
              <p><strong>Data records:</strong> CurrentYearPR: {currentYearPR?.length || 0}, AllTimeRank: {allTimeRank?.length || 0}</p>
            </div>
            <button 
              onClick={loadPowerRankingsData}
              className="px-6 py-3 bg-yellow-600 text-white rounded-xl hover:bg-yellow-700 transition-colors font-medium"
            >
              Reload Data
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      <div className="max-w-6xl mx-auto p-6 space-y-8">
        {/* Power Rankings Banner */}
        <PowerRankingsBanner />

        {/* Description Card */}
        <div className="bg-white rounded-2xl p-6 shadow-lg border border-slate-200">
          <p className="text-slate-600 leading-relaxed text-lg">
            {rankingsType === 'All-Time' 
              ? "All-Time Power Rankings captures manager consistency over time, but doesn't recognize playoff success. Navigate to the Legacy Score for a more comprehensive evaluation."
              : `${currentSeasonYear || new Date().getFullYear()} season power rankings based on cumulative all-play performance through the latest completed week.`}
          </p>
        </div>

        {/* Controls */}
        <div className="bg-white rounded-2xl shadow-lg p-6 border border-slate-200">
          <div className="flex flex-wrap gap-8 items-center justify-center">
            <div className="flex items-center space-x-4">
              <Filter className="w-5 h-5 text-slate-500" />
              <label className="text-sm font-semibold text-slate-700">Rankings Type:</label>
              <div className="flex bg-slate-100 rounded-xl p-1.5">
                {['Current Season', 'All-Time'].map((type) => (
                  <button
                    key={type}
                    onClick={() => setRankingsType(type)}
                    className={`px-4 py-2.5 rounded-lg text-sm font-medium transition-all duration-200 ${
                      rankingsType === type
                        ? 'bg-white text-indigo-600 shadow-md'
                        : 'text-slate-600 hover:text-slate-800'
                    }`}
                  >
                    {type}
                  </button>
                ))}
              </div>
            </div>

            <div className="flex items-center space-x-4">
              <Calendar className="w-5 h-5 text-slate-500" />
              <label className="text-sm font-semibold text-slate-700">Include Playoffs:</label>
              <div className="flex bg-slate-100 rounded-xl p-1.5">
                {['No', 'Yes'].map((option) => (
                  <button
                    key={option}
                    onClick={() => setIncludePlayoffs(option)}
                    className={`px-4 py-2.5 rounded-lg text-sm font-medium transition-all duration-200 ${
                      includePlayoffs === option
                        ? 'bg-white text-indigo-600 shadow-md'
                        : 'text-slate-600 hover:text-slate-800'
                    }`}
                  >
                    {option}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Rankings */}
        <div className="space-y-4">
          {rankingsData.map((manager, index) => {
            const lastWeekRank = getLastWeekRank(manager.Manager1);
            const rankChange = getRankChange(manager.Rank, lastWeekRank);
            const chartData = getManagerProgression(manager.Manager1);
            const winPct = (manager.cumWinPct || 0) * 100;
            const allTimeStats = getAllTimeStats(manager.Manager1);

            return (
              <div key={manager.Manager1} className="bg-white rounded-2xl shadow-lg border border-slate-200 hover:shadow-xl transition-all duration-300 hover:scale-[1.01] relative overflow-hidden">
                {/* Large Transparent Background Headshot for entire container */}
                <div 
                  className="absolute inset-0 z-0"
                  style={getManagerHeadshot(manager.Manager1, manager.Rank, true)}
                ></div>
                
                {/* Content overlay */}
                <div className="relative z-10 p-4 sm:p-6">
                  {/* Header - Enhanced Visual Hierarchy */}
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-4 sm:mb-6 space-y-3 sm:space-y-0">
                    {/* Left: Rank Badge with Manager Headshot and Info */}
                    <div className="flex items-center space-x-3 sm:space-x-4">
                      <div className="flex items-center space-x-2 sm:space-x-3">
                        {/* Primary Manager Headshot */}
                        <div className="relative group">
                          <img 
                            src={getManagerHeadshot(manager.Manager1, manager.Rank)} 
                            alt={`${manager.Manager1} headshot`}
                            className="w-12 h-12 sm:w-16 sm:h-16 rounded-full object-cover border-4 border-white shadow-lg cursor-pointer transition-all duration-200 group-hover:scale-105 group-hover:shadow-xl"
                            onClick={() => openModal(manager.Manager1, manager.Rank)}
                            onError={(e) => {
                              // Fallback to a default avatar if image fails to load
                              e.target.style.display = 'none';
                              e.target.nextSibling.style.display = 'flex';
                            }}
                          />
                          {/* Hover overlay with eye icon */}
                          <div className="absolute inset-0 bg-black/50 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-200 flex items-center justify-center cursor-pointer"
                               onClick={() => openModal(manager.Manager1, manager.Rank)}>
                            <Eye className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
                          </div>
                          {/* Fallback avatar */}
                          <div className="hidden w-12 h-12 sm:w-16 sm:h-16 rounded-full bg-gradient-to-br from-slate-400 to-slate-600 flex items-center justify-center border-4 border-white shadow-lg cursor-pointer"
                               onClick={() => openModal(manager.Manager1, manager.Rank)}>
                            <span className="text-white font-bold text-sm sm:text-base">
                              {manager.Manager1.charAt(0)}
                            </span>
                          </div>
                        </div>
                        
                        <div className={`${getRankBadgeColor(manager.Rank)} rounded-xl px-3 py-2 sm:px-4 sm:py-3 flex items-center space-x-2 shadow-lg`}>
                          {getRankIcon(manager.Rank)}
                          <span className="text-xl sm:text-2xl font-bold">{manager.Rank}</span>
                        </div>
                      </div>
                      
                      <div className="space-y-1 sm:space-y-3 flex-1">
                        <div className="flex items-center justify-between">
                          <h3 className="text-xl sm:text-2xl font-bold text-slate-800">{manager.Manager1}</h3>
                          
                          {/* Mobile: Small Last Week Pill */}
                          <div className="sm:hidden">
                            {rankChange ? (
                              <span className={`inline-flex items-center space-x-1 px-2 py-1 rounded-full text-xs font-bold ${
                                rankChange.direction === 'up' 
                                  ? 'bg-green-100 text-green-700' 
                                  : rankChange.direction === 'down' 
                                  ? 'bg-red-100 text-red-700'
                                  : 'bg-slate-100 text-slate-600'
                              }`}>
                                <span>{lastWeekRank || manager.Rank}</span>
                                {rankChange.direction === 'up' && <TrendingUp className="w-3 h-3" />}
                                {rankChange.direction === 'down' && <TrendingDown className="w-3 h-3" />}
                                {rankChange.direction === 'same' && <span className="text-xs">→</span>}
                              </span>
                            ) : (
                              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-bold bg-slate-100 text-slate-600">
                                {lastWeekRank || manager.Rank}
                              </span>
                            )}
                          </div>
                        </div>
                        
                        {/* Primary Records Row - Mobile Stacked, Desktop Horizontal */}
                        <div className="space-y-1 sm:space-y-0 sm:flex sm:items-center sm:space-x-8 text-sm">
                          {/* Traditional H2H Record */}
                          <div className="flex items-center space-x-1">
                            <span className="text-slate-500 font-medium text-xs sm:text-sm">Record:</span>
                            <span className="font-bold text-slate-800 text-sm sm:text-base">
                              {rankingsType === 'All-Time' 
                                ? `(${manager.traditionalWins || 0} - ${manager.traditionalLosses || 0})`
                                : `(${manager.actualWins || 0} - ${manager.actualLosses || 0})`
                              }
                            </span>
                          </div>

                          {/* All-Play Record with Enhanced Styling */}
                          <div className="flex items-center space-x-1">
                            <span className="text-slate-500 font-medium text-xs sm:text-sm">All-Play:</span>
                            <span className="font-bold text-slate-800 text-sm sm:text-base">
                              {rankingsType === 'All-Time' 
                                ? `${manager.cumWins}-${manager.cumLosses}`
                                : `${manager.cumWins || 0}-${manager.cumLosses || 0}`
                              }
                            </span>
                            <span className={`font-bold text-sm sm:text-base ml-1 ${getPerformanceColor(winPct)}`}>
                              ({winPct.toFixed(1)}%)
                            </span>
                          </div>

                          {/* PF (Points For) - Current Season Only */}
                          {rankingsType === 'Current Season' && manager.PF && (
                            <div className="flex items-center space-x-1">
                              <span className="text-slate-500 font-medium text-xs sm:text-sm">PF:</span>
                              <span className="font-bold text-slate-800 text-sm sm:text-base">{manager.PF.toFixed(2)}</span>
                            </div>
                          )}
                        </div>

                        {/* Secondary Stats Row - All-Time Only - Mobile Condensed */}
                        {rankingsType === 'All-Time' && allTimeStats && (
                          <div className="space-y-1 sm:space-y-0 sm:flex sm:items-center sm:space-x-6 text-xs sm:text-sm pt-1 sm:pt-1 border-t border-slate-100">
                            {/* Game counts row */}
                            <div className="flex items-center space-x-3 sm:space-x-4">
                              <div className="flex items-center space-x-1">
                                <span className="text-slate-500 font-medium">All-Play:</span>
                                <span className="font-semibold text-slate-600">{formatTotalGames(allTimeStats.totalGames)}</span>
                              </div>
                              <div className="flex items-center space-x-1">
                                <span className="text-slate-500 font-medium">H2H:</span>
                                <span className="font-semibold text-slate-600">
                                  {manager.traditionalWins + manager.traditionalLosses}
                                </span>
                              </div>
                            </div>
                            
                            {/* Peak/Trough row */}
                            <div className="flex items-center space-x-3 sm:space-x-4">
                              <div className="flex items-center space-x-1">
                                <span className="text-slate-500 font-medium">Peak:</span>
                                <span className="font-bold text-emerald-600">#{allTimeStats.bestRank}</span>
                                {allTimeStats.peakDate && (
                                  <span className="text-xs text-slate-500 ml-1 hidden sm:inline">({allTimeStats.peakDate})</span>
                                )}
                              </div>
                              <div className="flex items-center space-x-1">
                                <span className="text-slate-500 font-medium">Trough:</span>
                                <span className="font-bold text-red-600">#{allTimeStats.worstRank}</span>
                                {allTimeStats.troughDate && (
                                  <span className="text-xs text-slate-500 ml-1 hidden sm:inline">({allTimeStats.troughDate})</span>
                                )}
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Desktop: Enhanced Movement Indicator */}
                    <div className="hidden sm:block">
                      <div className="text-right bg-slate-50 rounded-xl px-4 py-3 border border-slate-200">
                        <div className="text-xs text-slate-500 uppercase tracking-wide font-medium mb-1">
                          Last Week
                        </div>
                        <div className="text-lg font-bold text-slate-700 mb-1">
                          {lastWeekRank || manager.Rank}
                        </div>
                        <div className="text-sm font-medium">
                          {rankChange ? (
                            <span className={`inline-flex items-center space-x-1 px-2 py-1 rounded-full text-xs font-bold ${
                              rankChange.direction === 'up' 
                                ? 'bg-green-100 text-green-700' 
                                : rankChange.direction === 'down' 
                                ? 'bg-red-100 text-red-700'
                                : 'bg-slate-100 text-slate-600'
                            }`}>
                              <span>({formatRankChange(rankChange)})</span>
                              {rankChange.direction === 'up' && <TrendingUp className="w-3 h-3" />}
                              {rankChange.direction === 'down' && <TrendingDown className="w-3 h-3" />}
                            </span>
                          ) : (
                            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-bold bg-slate-100 text-slate-600">
                              (Even)
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Chart Section with Maximized Mobile Space */}
                  {chartData.length > 1 && (
                    <div className="bg-white/80 backdrop-blur-sm rounded-lg border border-slate-200 p-3 sm:p-4 shadow-sm relative">
                      {/* Chart Title for Context - Mobile Condensed */}
                      <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-2 sm:mb-4 space-y-1 sm:space-y-0">
                        <h4 className="font-semibold text-slate-700 flex items-center space-x-2 text-sm sm:text-base">
                          {rankingsType === 'All-Time' ? (
                            <>
                              <History className="w-4 h-4 text-slate-500" />
                              <span>{manager.Manager1}'s All-Time Journey</span>
                            </>
                          ) : (
                            <>
                              <BarChart3 className="w-4 h-4 text-slate-500" />
                              <span>Season Progression</span>
                            </>
                          )}
                        </h4>
                        {rankingsType === 'All-Time' && allTimeStats && (
                          <div className="text-xs text-slate-500 bg-white/80 backdrop-blur-sm px-2 py-1 rounded-md border border-slate-200">
                            <span className="sm:hidden">{allTimeStats.totalYears}y • {formatTotalGames(allTimeStats.totalGames)}</span>
                            <span className="hidden sm:inline">{allTimeStats.totalYears} seasons • {formatTotalGames(allTimeStats.totalGames)} all-play games</span>
                          </div>
                        )}
                      </div>

                      {/* Charts with Maximized Mobile Height */}
                      {rankingsType === 'All-Time' ? (
                        <div className="h-48 sm:h-48">
                          <ResponsiveContainer width="100%" height="100%">
                              <ComposedChart data={chartData} margin={{ top: 10, right: 10, left: 20, bottom: 15 }}>
                                <defs>
                                  <linearGradient id={`gradient-${manager.Manager1}`} x1="0%" y1="0%" x2="100%" y2="0%">
                                    {createDynamicGradient(chartData, manager.Manager1)?.map((stop, index) => (
                                      <stop key={index} offset={stop.offset} stopColor={stop.color} />
                                    ))}
                                  </linearGradient>
                                  <linearGradient id={`area-${manager.Manager1}`} x1="0%" y1="0%" x2="100%" y2="0%">
                                    {createDynamicGradient(chartData, manager.Manager1)?.map((stop, index) => (
                                      <stop key={index} offset={stop.offset} stopColor={stop.color} stopOpacity={0.15} />
                                    ))}
                                  </linearGradient>
                                </defs>
                                <CartesianGrid strokeDasharray="2 2" stroke="#e2e8f0" opacity={0} />
                                <XAxis 
                                  dataKey="timePoint" 
                                  axisLine={false}
                                  tickLine={false}
                                  tick={{ fontSize: '10px', fill: '#64748b', fontWeight: '500' }}
                                  tickFormatter={(value) => {
                                    const year = chartData.find(d => d.timePoint === value)?.year;
                                    return year && value % 30 === 0 ? year : '';
                                  }}
                                />
                                <YAxis 
                                  domain={[1, 13]} 
                                  reversed={true}
                                  axisLine={false}
                                  tickLine={false}
                                  tick={{ fontSize: '10px', fill: '#64748b', fontWeight: '500' }}
                                  tickFormatter={(value) => `${value}`}
                                  label={{ value: 'Rank', angle: -90, position: 'insideLeft', style: { textAnchor: 'middle', fill: '#64748b', fontSize: '11px', fontWeight: '600' } }}
                                />
                                
                                {/* Background area with gradient shading */}
                                <Area
                                  type="monotone"
                                  dataKey="rank"
                                  stroke="none"
                                  fill={`url(#area-${manager.Manager1})`}
                                />
                                
                                {/* Main trend line with gradient */}
                                <Line
                                  type="monotone"
                                  dataKey="rank"
                                  stroke={`url(#gradient-${manager.Manager1})`}
                                  strokeWidth={2}
                                  dot={(props) => {
                                    const { cx, cy, payload, index } = props;
                                    if (!payload || index === undefined) return null;
                                    
                                    // Calculate the gradient color for this specific point
                                    const gradientStops = createDynamicGradient(chartData, manager.Manager1);
                                    if (!gradientStops || gradientStops.length === 0) return null;
                                    
                                    const percentage = (index / (chartData.length - 1)) * 100;
                                    let dotColor = gradientStops[0].color;
                                    
                                    // Find the appropriate color from gradient stops
                                    for (let i = 0; i < gradientStops.length - 1; i++) {
                                      const currentStop = parseFloat(gradientStops[i].offset.replace('%', ''));
                                      const nextStop = parseFloat(gradientStops[i + 1].offset.replace('%', ''));
                                      
                                      if (percentage >= currentStop && percentage <= nextStop) {
                                        dotColor = gradientStops[i].color;
                                        break;
                                      } else if (percentage > parseFloat(gradientStops[gradientStops.length - 1].offset.replace('%', ''))) {
                                        dotColor = gradientStops[gradientStops.length - 1].color;
                                      }
                                    }
                                    
                                    return (
                                      <circle
                                        cx={cx}
                                        cy={cy}
                                        r={1.5}
                                        fill={dotColor}
                                        stroke="#fff"
                                        strokeWidth={0.5}
                                        opacity={0.8}
                                      />
                                    );
                                  }}
                                  activeDot={{ 
                                    r: 3, 
                                    stroke: `url(#gradient-${manager.Manager1})`, 
                                    strokeWidth: 2, 
                                    fill: "#fff" 
                                  }}
                                />
                                
                                <Tooltip 
                                  content={({ active, payload, label }) => {
                                    if (active && payload && payload.length) {
                                      const point = chartData.find(d => d.timePoint === label);
                                      const rank = payload[0].value;
                                      return (
                                        <div style={{
                                          backgroundColor: 'rgba(255, 255, 255, 0.98)',
                                          border: '1px solid #e2e8f0',
                                          borderRadius: '8px',
                                          fontSize: '13px',
                                          boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                                          padding: '8px 12px'
                                        }}>
                                          <p style={{ margin: 0, fontWeight: 'bold' }}>
                                            Rank {rank}
                                          </p>
                                          <p style={{ margin: 0, color: '#64748b', fontSize: '12px' }}>
                                            {point ? `${point.year} Week ${point.week}` : `Timeline ${label}`}
                                          </p>
                                        </div>
                                      );
                                    }
                                    return null;
                                  }}
                                />
                              </ComposedChart>
                            </ResponsiveContainer>
                          </div>
                        ) : (
                          // Current Season Chart with Maximized Mobile Height
                          <div className="h-40 sm:h-36">
                            <ResponsiveContainer width="100%" height="100%">
                              <LineChart data={chartData} margin={{ top: 15, right: 15, left: 20, bottom: 20 }}>
                                <defs>
                                  <linearGradient id={`current-gradient-${manager.Manager1}`} x1="0%" y1="0%" x2="100%" y2="0%">
                                    {createDynamicGradient(chartData.map(d => ({ rank: 13 - d.displayRank })), manager.Manager1)?.map((stop, index) => (
                                      <stop key={index} offset={stop.offset} stopColor={stop.color} />
                                    ))}
                                  </linearGradient>
                                </defs>
                                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" opacity={0} />
                                <XAxis 
                                  dataKey="week" 
                                  axisLine={false}
                                  tickLine={false}
                                  tick={{ fontSize: '11px', fill: '#64748b', fontWeight: '500' }}
                                  label={{ value: 'Week', position: 'insideBottom', offset: -5, style: { textAnchor: 'middle', fill: '#64748b', fontSize: '11px', fontWeight: '600' } }}
                                />
                                <YAxis 
                                  domain={[0, 13]} 
                                  axisLine={false}
                                  tickLine={false}
                                  tick={{ fontSize: '11px', fill: '#64748b', fontWeight: '500' }}
                                  tickFormatter={(value) => 13 - value}
                                  label={{ value: 'Rank', angle: -90, position: 'insideLeft', style: { textAnchor: 'middle', fill: '#64748b', fontSize: '11px', fontWeight: '600' } }}
                                />
                                <Line
                                  type="monotone"
                                  dataKey="displayRank"
                                  stroke={`url(#current-gradient-${manager.Manager1})`}
                                  strokeWidth={4}
                                  dot={(props) => {
                                    const { cx, cy, payload } = props;
                                    if (!payload) return null;
                                    return (
                                      <circle
                                        cx={cx}
                                        cy={cy}
                                        r={3}
                                        fill={getRankColor(13 - payload.displayRank)}
                                        stroke="#fff"
                                        strokeWidth={2}
                                      />
                                    );
                                  }}
                                  activeDot={{
                                    r: 5,
                                    stroke: getRankColor(manager.Rank),
                                    strokeWidth: 3,
                                    fill: "#fff"
                                  }}
                                />
                                <Tooltip 
                                  content={({ active, payload, label }) => {
                                    if (active && payload && payload.length) {
                                      const rank = 13 - payload[0].value;
                                      return (
                                        <div style={{
                                          backgroundColor: 'rgba(255, 255, 255, 0.98)',
                                          border: '1px solid #e2e8f0',
                                          borderRadius: '8px',
                                          fontSize: '14px',
                                          boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                                          padding: '10px 14px'
                                        }}>
                                          <p style={{ margin: 0, fontWeight: 'bold' }}>
                                            Rank {rank}
                                          </p>
                                          <p style={{ margin: 0, color: '#64748b', fontSize: '12px' }}>
                                            Week {label}
                                          </p>
                                        </div>
                                      );
                                    }
                                    return null;
                                  }}
                                />
                              </LineChart>
                            </ResponsiveContainer>
                          </div>
                        )}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {/* Footer */}
        <div className="bg-white rounded-2xl p-6 shadow-lg border border-slate-200 text-center">
          <p className="text-slate-600 leading-relaxed">
            <strong>Power Rankings Methodology:</strong> Rankings calculated using the "all-play" system where each manager's weekly score is compared against every other manager's score that week. 
            {rankingsType === 'All-Time' && ' All-time rankings represent cumulative performance across all seasons and leagues.'} 
            Ties are broken by total points scored.
          </p>
        </div>

        {/* Modal for Headshot Viewing */}
        {isModalOpen && modalImage && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm p-4">
            <div className="relative max-w-2xl w-full max-h-[90vh] bg-white rounded-2xl shadow-2xl overflow-hidden">
              {/* Modal Header */}
              <div className="flex items-center justify-between p-4 sm:p-6 bg-gradient-to-r from-slate-50 to-slate-100 border-b border-slate-200">
                <div>
                  <h3 className="text-xl sm:text-2xl font-bold text-slate-800">{modalImage.manager}</h3>
                  <p className="text-sm text-slate-600 mt-1">
                    Current Rank: #{rankingsData.find(m => m.Manager1 === modalImage.manager)?.Rank || 'N/A'} • 
                    {modalImage.rank >= 1 && modalImage.rank <= 5 && ' Feeling Happy 😊'}
                    {modalImage.rank >= 6 && modalImage.rank <= 7 && ' Staying Focused 😐'}
                    {modalImage.rank >= 8 && modalImage.rank <= 12 && ' Getting Angry 😠'}
                  </p>
                </div>
                <button
                  onClick={closeModal}
                  className="p-2 rounded-full bg-slate-200 hover:bg-slate-300 transition-colors"
                  aria-label="Close modal"
                >
                  <X className="w-5 h-5 text-slate-600" />
                </button>
              </div>

              {/* Modal Content */}
              <div className="p-6 sm:p-8 text-center">
                <div className="relative inline-block">
                  <img 
                    src={modalImage.src}
                    alt={`${modalImage.manager} headshot`}
                    className="max-w-full max-h-96 w-auto h-auto rounded-xl shadow-lg"
                    onError={(e) => {
                      e.target.style.display = 'none';
                      e.target.nextSibling.style.display = 'flex';
                    }}
                  />
                  {/* Fallback for modal */}
                  <div className="hidden w-96 h-96 bg-gradient-to-br from-slate-400 to-slate-600 rounded-xl shadow-lg flex items-center justify-center">
                    <span className="text-white font-bold text-6xl">
                      {modalImage.manager.charAt(0)}
                    </span>
                  </div>
                </div>

                <div className="mt-6 text-sm text-slate-500">
                  Click outside or press ESC to close
                </div>
              </div>
            </div>

            {/* Click outside to close */}
            <div 
              className="absolute inset-0 -z-10" 
              onClick={closeModal}
            ></div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PowerRankings;